<script setup lang="ts">
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue'
import { useSelectionStore } from '@/stores/selection'
import { useQueryClient } from '@tanstack/vue-query'
import * as api from '@/api/endpoints'
import {
  useInitMasters,
  useOfferDetail,
  useJudgmentOverview,
  useJudgeOffersMutation,
  useSaveMemoMutation,
  useUpdateOfferFlagsMutation,
  useApplyRenewalMutation,
} from '@/api/queries'
import type { JudgeAction, JudgmentOverviewItem } from '@/types/api'
import { useMemoAutocomplete } from '@/composables/useMemoAutocomplete'
import { useToastStore } from '@/stores/toast'
import { useConfirmModalStore } from '@/stores/confirmModal'
import { useResultModalStore } from '@/stores/resultModal'
import JudgmentQueueApplyModal from './JudgmentQueueApplyModal.vue'

const selection = useSelectionStore()
const asinRef = computed(() => selection.selectedAsin)
const sellerIdRef = computed(() => selection.selectedSellerId)
const { data: detail } = useOfferDetail(asinRef, sellerIdRef)
const { data: masters } = useInitMasters()

const memoHcube = ref('')
const memoSeller = ref('')

watch(
  () => detail.value?.offer,
  (offer) => {
    memoHcube.value = offer?.MemoHcube ?? ''
    memoSeller.value = offer?.Memo ?? ''
  },
)

const judgeMutation = useJudgeOffersMutation()
const saveMemoMutation = useSaveMemoMutation()
const flagsMutation = useUpdateOfferFlagsMutation()
const renewalMutation = useApplyRenewalMutation()
const queryClient = useQueryClient()
/** チェックした複数商品への一括処理(更新申請・一時停止・販売無効化・削除・保存)の実行中 */
const bulkRunning = ref(false)

// 一覧(検索結果)。一括処理で各商品の今の状態(一時停止中か等)を見るために使う。
// OfferTable と同じ条件なので、キャッシュを共有して追加の通信は起きない
const overviewParams = computed(() => selection.effectiveParams)
const { data: overview } = useJudgmentOverview(overviewParams)

const anyPending = computed(
  () =>
    bulkRunning.value ||
    judgeMutation.isPending.value ||
    saveMemoMutation.isPending.value ||
    flagsMutation.isPending.value ||
    renewalMutation.isPending.value,
)

const statusLabel: Record<string, string> = {
  '1': '審査中',
  '2': '審査OK',
  '8': '出品NG',
  '9': '審査NG',
}

// ---- トースト通知(軽微な通知のみ。重要な処理結果は下のresultModalを使う) ----
const toastStore = useToastStore()
function showToast(message: string, type: 'ok' | 'ng' = 'ok') {
  toastStore.show(message, type)
}

// ---- 確認モーダル / 結果表示 ----
// DB更新を伴う処理は、実行前の確認をモーダル(confirmModal)で行う。
//
// 【結果の出し分け】
//   成功 → 画面下のトースト(数秒で自動的に消える)
//   失敗 → 画面中央のモーダル(操作して閉じるまで残る)
// 審査OK/NGは連続で何十件も処理するため、成功のたびにモーダルを閉じる操作が
// 入ると作業のテンポを大きく損なう。一方で失敗は必ず気づいてほしいので
// モーダルのまま残す。
const confirmModal = useConfirmModalStore()
const resultModal = useResultModalStore()
function showResult(message: string, type: 'ok' | 'ng' = 'ok') {
  if (type === 'ng') {
    resultModal.show(message, type)
  } else {
    toastStore.show(message, type)
  }
}
// ---- 対象オファーの解決 ----
// 単一選択の場合は複合キー(condition/fulfillment)まで含めて厳密に対象を絞る。
// 一括選択の場合は checkedTargets(sellerId+asin) を使う。
function singleTarget() {
  const offer = detail.value?.offer
  if (selection.selectedSellerId && selection.selectedAsin) {
    return {
      sellerId: selection.selectedSellerId,
      asin: selection.selectedAsin,
      conditionCode: offer?.ConditionCode,
      fulfillmentChannelCode: offer?.FulfillmentChannelCode,
      taxId: selection.selectedTaxId ?? undefined,
    }
  }
  return null
}

interface JudgeTarget {
  sellerId: string
  asin: string
  /** 一括選択時は一覧に無いため未指定になる。指定時のみWHERE条件に加わる */
  conditionCode?: string
  fulfillmentChannelCode?: string
  taxId?: number
}

function judgeTargets(): JudgeTarget[] {
  const checked = selection.checkedTargets()
  if (checked.length > 0) return checked
  const single = singleTarget()
  return single ? [single] : []
}

async function confirmAndJudge(action: JudgeAction, label: string) {
  const list = judgeTargets()
  if (list.length === 0) {
    showToast('対象の商品が選択されていません。', 'ng')
    return
  }
  if (!(await confirmModal.ask(`選択中の${list.length}件を「${label}」にしてよろしいですか？`))) return

  // 【VB.NET版の挙動】GetSelectedOfferList() が offer.Memo / offer.MemoHcube に
  // 画面のテキストボックスの内容を詰めてから UpdateOffer() に渡すため、
  // 「保存」ボタンを押さなくても審査OK/NG等の判定時にメモが保存される。
  // 複数選択時は、選択した全商品に同じメモが入る。
  const offersWithMemo = list.map((o) => ({
    ...o,
    memoHcube: memoHcube.value,
    memoSeller: memoSeller.value,
  }))

  judgeMutation.mutate(
    { action, offers: offersWithMemo },
    {
      onSuccess: (result) => {
        showResult(`「${label}」完了：${result.successCount}件成功 / ${result.failureCount}件失敗`)
        selection.clearChecked()
      },
      onError: (err) => showResult(`処理に失敗しました: ${(err as Error).message}`, 'ng'),
    },
  )
}

/**
 * メモの保存。
 * 【VB.NET版の挙動】チェックボックスで複数選択されている場合は、
 * GetSelectedOfferList() がチェック済みの全行を返すため、
 * 入力したメモが選択中の全商品に保存される。
 * 未チェックのときだけ、現在表示中の1件が対象になる。
 */
async function save() {
  const list = judgeTargets()
  if (list.length === 0) {
    showToast('対象の商品が選択されていません。', 'ng')
    return
  }

  // 複数件に同じ内容を書き込むため、意図しない一括上書きを防ぐ確認を挟む
  if (list.length > 1) {
    if (!(await confirmModal.ask(
      `選択中の${list.length}件すべてに、入力した内容を保存します。よろしいですか？`,
    ))) return
  }

  const checked = selection.checkedCount > 0
  bulkRunning.value = list.length > 1
  let results: PromiseSettledResult<unknown>[]
  try {
    results = await Promise.allSettled(
      list.map((t) =>
        saveMemoMutation.mutateAsync({
          asin: t.asin,
          req: {
            sellerId: t.sellerId,
            memoHcube: memoHcube.value,
            memoSeller: memoSeller.value,
            conditionCode: t.conditionCode,
            fulfillmentChannelCode: t.fulfillmentChannelCode,
            taxId: t.taxId,
          },
        }),
      ),
    )
  } finally {
    bulkRunning.value = false
  }
  // 処理が終わったらチェックを外す(審査OK等と同じ)
  if (checked) selection.clearChecked()
  const failed = list.filter((_, i) => results[i].status === 'rejected')
  if (failed.length === 0) {
    showResult(list.length > 1 ? `${list.length}件にメモを保存しました` : 'メモを保存しました')
  } else {
    showResult(`保存に失敗しました：${list.length - failed.length}件成功 / ${failed.length}件失敗${failedAsins(failed)}`, 'ng')
  }
}

// ---- チェックした複数商品への一括処理 ----
// 更新申請・一時停止・販売無効化・削除は、チェックがあればチェックした商品すべて、
// 無ければ詳細を表示している1件が対象(審査OK・保存と同じ)。

/** 一括処理の対象1行(一覧の行。コンディション違いで同じASINが複数行あれば行ごと) */
interface BulkRow {
  sellerId: string
  asin: string
  conditionCode?: string
  fulfillmentChannelCode?: string
  /** 一覧に無い(条件を変えて見えなくなった)ときは不明 */
  deleteFlg?: '0' | '1' | '2'
  enableFlg?: '0' | '1'
}

function checkedRows(): BulkRow[] {
  const items: JudgmentOverviewItem[] = overview.value?.items ?? []
  const rows: BulkRow[] = []
  for (const t of selection.checkedTargets()) {
    const hits = items.filter((i) => i.sellerId === t.sellerId && i.childAsin === t.asin)
    if (hits.length === 0) {
      rows.push({ sellerId: t.sellerId, asin: t.asin })
      continue
    }
    // 一覧は親ASIN違いで同じ出品が複数行出ることがあるので、出品単位で1回にする
    const seen = new Set<string>()
    for (const i of hits) {
      const k = `${i.conditionCode}:${i.fulfillmentChannelCode}`
      if (seen.has(k)) continue
      seen.add(k)
      rows.push({
        sellerId: i.sellerId,
        asin: i.childAsin,
        conditionCode: i.conditionCode,
        fulfillmentChannelCode: i.fulfillmentChannelCode,
        deleteFlg: i.deleteFlg,
        enableFlg: i.enableFlg,
      })
    }
  }
  return rows
}

function failedAsins(rows: { asin: string }[]): string {
  if (rows.length === 0) return ''
  const shown = rows.slice(0, 10).map((r) => r.asin).join(', ')
  return `\n失敗: ${shown}${rows.length > 10 ? ` ほか${rows.length - 10}件` : ''}`
}

/** 同時に送る件数(サーバー・DBに一度に負荷をかけすぎないため) */
const BULK_CONCURRENCY = 5

async function runBulk<T>(rows: T[], fn: (row: T) => Promise<unknown>): Promise<T[]> {
  const failed: T[] = []
  let next = 0
  async function worker() {
    while (next < rows.length) {
      const row = rows[next++]
      try {
        await fn(row)
      } catch (e) {
        console.warn('一括処理の失敗', row, e)
        failed.push(row)
      }
    }
  }
  bulkRunning.value = true
  try {
    await Promise.all(Array.from({ length: Math.min(BULK_CONCURRENCY, rows.length) }, worker))
  } finally {
    bulkRunning.value = false
    queryClient.invalidateQueries({ queryKey: ['offers'] })
    queryClient.invalidateQueries({ queryKey: ['offerDetail'] })
  }
  return failed
}

/**
 * チェックした商品への一括処理の共通部分。
 *   eligible: 対象にする行(今の状態では意味の無い行は除く。例: 一時停止は通常の行だけ。状態が分からない行は対象にする)
 */
async function bulkOperate(
  label: string,
  eligible: (r: BulkRow) => boolean,
  call: (r: BulkRow) => Promise<unknown>,
  note = '',
) {
  const all = checkedRows()
  const rows = all.filter(eligible)
  const skipped = all.length - rows.length
  if (rows.length === 0) {
    showToast(`チェックした商品に「${label}」の対象がありません（今の状態では対象になりません）。`, 'ng')
    return
  }
  const skipNote = skipped > 0 ? `\n（今の状態では対象にならない${skipped}件は除きます）` : ''
  if (!(await confirmModal.ask(`チェックした${rows.length}件を「${label}」してよろしいですか？${note}${skipNote}`))) return
  const failed = await runBulk(rows, call)
  selection.clearChecked()
  if (failed.length === 0) {
    showResult(`「${label}」完了：${rows.length}件${skipped > 0 ? `（${skipped}件は対象外）` : ''}`)
  } else {
    showResult(`「${label}」：${rows.length - failed.length}件成功 / ${failed.length}件失敗${failedAsins(failed)}`, 'ng')
  }
}

function bulkFlags(
  label: string,
  req: { deleteFlg?: 'DELETED' | 'PAUSE' | 'UNDELETE'; currentDeleteFlg?: 'PAUSE' | 'DELETED'; enableFlg?: 'ENABLE' | 'DISABLE' },
  eligible: (r: BulkRow) => boolean,
) {
  return bulkOperate(label, eligible, (r) =>
    api.updateOfferFlags(r.asin, {
      sellerId: r.sellerId,
      conditionCode: r.conditionCode,
      fulfillmentChannelCode: r.fulfillmentChannelCode,
      // 審査OK等と同じく、チェックした全商品に内部連絡事項・セラーへのお知らせの入力内容も保存する
      memoHcube: memoHcube.value,
      memoSeller: memoSeller.value,
      ...req,
    }),
    '\n内部連絡事項・セラーへのお知らせの入力内容も、この全商品に保存します。',
  )
}
const isBulk = computed(() => selection.checkedCount > 0)

// ---- フラグ操作(削除・一時停止・販売無効化) ----
async function updateFlag(
  label: string,
  req: {
    deleteFlg?: 'DELETED' | 'PAUSE' | 'UNDELETE'
    /** UNDELETE時の解除前の状態。操作ログ名の決定に使う */
    currentDeleteFlg?: 'PAUSE' | 'DELETED'
    enableFlg?: 'ENABLE' | 'DISABLE'
  },
  confirmMessage: string,
) {
  const t = singleTarget()
  if (!t) {
    showToast('対象の商品が選択されていません。', 'ng')
    return
  }
  if (!(await confirmModal.ask(confirmMessage))) return
  flagsMutation.mutate(
    {
      asin: t.asin,
      req: {
        sellerId: t.sellerId,
        conditionCode: t.conditionCode,
        fulfillmentChannelCode: t.fulfillmentChannelCode,
        // 審査OK等と同じく、内部連絡事項・セラーへのお知らせの入力内容も保存する
        memoHcube: memoHcube.value,
        memoSeller: memoSeller.value,
        ...req,
      },
    },
    {
      onSuccess: () => showResult(`「${label}」を実行しました`),
      onError: (err) => showResult(`失敗しました: ${(err as Error).message}`, 'ng'),
    },
  )
}

// 「一時停止」ボタン: VB版 ButtonStop_Click と同様、現在の状態によって動作が変わる
// トグルボタン。DeleteFlg: '0'=通常 / '1'=削除済み / '2'=一時停止中
const stopButtonState = computed(() => {
  const flg = detail.value?.offer?.DeleteFlg
  if (flg === '2') {
    // 一時停止中 → 解除
    // currentDeleteFlg は操作ログ名の決定に使う(VB版は解除前の状態で Pause/Deleted を使い分ける)
    return { label: '一時停止解除', variant: 'undo', deleteFlg: 'UNDELETE' as const, currentDeleteFlg: 'PAUSE' as const, confirm: '一時販売停止を解除してよろしいですか？' }
  }
  if (flg === '1') {
    // 削除済み → 復活
    return { label: '復活', variant: 'undo', deleteFlg: 'UNDELETE' as const, currentDeleteFlg: 'DELETED' as const, confirm: 'この商品を復活させてよろしいですか？' }
  }
  // 通常 → 一時停止
  return { label: '一時停止', variant: 'normal', deleteFlg: 'PAUSE' as const, currentDeleteFlg: undefined, confirm: '販売を一時停止してよろしいですか？' }
})
function toggleStop() {
  const s = stopButtonState.value
  if (isBulk.value) {
    // ボタンの表示(詳細を表示中の商品の状態)と同じ操作を、チェックした商品のうち対象になるものに行う
    const from = s.deleteFlg === 'PAUSE' ? '0' : s.currentDeleteFlg === 'PAUSE' ? '2' : '1'
    return bulkFlags(s.label, { deleteFlg: s.deleteFlg, currentDeleteFlg: s.currentDeleteFlg }, (r) => !r.deleteFlg || r.deleteFlg === from)
  }
  updateFlag(s.label, { deleteFlg: s.deleteFlg, currentDeleteFlg: s.currentDeleteFlg }, s.confirm)
}

// 「販売無効化」ボタン: VB版 ButtonEnabled_Click と同様、現在の状態によって動作が変わる
// トグルボタン。EnableFlg: '1'=有効(出品中) / '0'=無効
const enableButtonState = computed(() => {
  const flg = detail.value?.offer?.EnableFlg
  if (flg === '0') {
    // 無効 → 有効化
    return { label: '販売有効化', variant: 'undo', enableFlg: 'ENABLE' as const, confirm: 'この商品の販売を再度有効にしてよろしいですか？' }
  }
  // 有効 → 無効化
  return { label: '販売無効化', variant: 'normal', enableFlg: 'DISABLE' as const, confirm: 'この商品を販売無効化してよろしいですか？' }
})
function toggleEnable() {
  const s = enableButtonState.value
  if (isBulk.value) {
    const from = s.enableFlg === 'DISABLE' ? '1' : '0'
    return bulkFlags(s.label, { enableFlg: s.enableFlg }, (r) => !r.enableFlg || r.enableFlg === from)
  }
  updateFlag(s.label, { enableFlg: s.enableFlg }, s.confirm)
}

// 「削除」ボタン: 既に削除済みなら操作の必要がないため無効化する(わかりやすさのため)
const isAlreadyDeleted = computed(() => detail.value?.offer?.DeleteFlg === '1')
function deleteOffer() {
  if (isBulk.value) return bulkFlags('削除', { deleteFlg: 'DELETED' }, (r) => r.deleteFlg !== '1')
  updateFlag('削除', { deleteFlg: 'DELETED' }, 'この商品を削除してよろしいですか？')
}

// ---- 更新申請 ----
async function applyRenewal() {
  if (isBulk.value) {
    // 更新申請は SellerId + ASIN 単位(コンディション違いの行はまとめて1回)
    const seen = new Set<string>()
    return bulkOperate(
      '更新申請',
      (r) => {
        const k = `${r.sellerId}:${r.asin}`
        if (seen.has(k)) return false
        seen.add(k)
        return true
      },
      (r) => api.applyRenewal(r.asin, { sellerId: r.sellerId }),
    )
  }
  const t = singleTarget()
  if (!t) {
    showToast('対象の商品が選択されていません。', 'ng')
    return
  }
  if (!(await confirmModal.ask('この商品を「更新申請」してよろしいですか？（審査中(準備)に戻ります）'))) return
  renewalMutation.mutate(
    { asin: t.asin, req: { sellerId: t.sellerId } },
    {
      onSuccess: () => showResult('更新申請を実行しました'),
      onError: (err) => showResult(`失敗しました: ${(err as Error).message}`, 'ng'),
    },
  )
}

// ---- ジャッジ待ち申請 ----
// チェックがあればチェックした商品(ASINを重複なく)、無ければ詳細を表示している1件を申請画面に入れて開く。
// 申請画面でASINを確認・追加してから申請する(結果は受理/却下を1件ずつ表示)。
const judgmentQueueAsins = ref<string | null>(null)
function openJudgmentQueue() {
  const asins = selection.checkedCount > 0
    ? [...new Set(selection.checkedTargets().map((t) => t.asin))]
    : selection.selectedAsin ? [selection.selectedAsin] : []
  judgmentQueueAsins.value = asins.join('\n')
}
function onJudgmentQueueApplied() {
  // 処理後はチェックを外す(審査OK等と同じ)
  selection.clearChecked()
}

// ---- コメントテンプレート挿入 ----
function insertInternalTemplate(e: Event) {
  const id = (e.target as HTMLSelectElement).value
  if (!id) return
  const tpl = masters.value?.commentTemplatesInternal.find((t) => t.TemplateId === id)
  if (tpl) memoHcube.value = memoHcube.value ? `${memoHcube.value}\n${tpl.Body}` : tpl.Body
  ;(e.target as HTMLSelectElement).value = ''
}
function insertSellerTemplate(e: Event) {
  const id = (e.target as HTMLSelectElement).value
  if (!id) return
  const tpl = masters.value?.commentTemplatesSeller.find((t) => t.TemplateId === id)
  if (tpl) memoSeller.value = memoSeller.value ? `${memoSeller.value}\n${tpl.Body}` : tpl.Body
  ;(e.target as HTMLSelectElement).value = ''
}

const hasSelection = computed(() => !!selection.selectedAsin && !!selection.selectedSellerId)
/** 対象がある(詳細を表示中 or チェックあり) */
const hasTarget = computed(() => hasSelection.value || selection.checkedCount > 0)

// ---- メモ欄の入力補完(「//」で日付、「@」で自分の名前) ----
const internalTextarea = ref<HTMLTextAreaElement | null>(null)
const sellerTextarea = ref<HTMLTextAreaElement | null>(null)
const internalAc = useMemoAutocomplete(memoHcube, internalTextarea, true)
// セラー向けの文面に審査担当者名を入れることは無いので「@」は無効化する
const sellerAc = useMemoAutocomplete(memoSeller, sellerTextarea, false)

// ---- 表示モード ----
// 下ペインの高さを抑えられるようにする。
//   normal  : 通常(textarea 150px)
//   compact : 高さを詰める(textarea 64px)
const MODES = ['normal', 'compact'] as const
type BarMode = (typeof MODES)[number]
const STORAGE_KEY = 'salmon.actionbar.mode'

function loadMode(): BarMode {
  const v = localStorage.getItem(STORAGE_KEY)
  return (MODES as readonly string[]).includes(v ?? '') ? (v as BarMode) : 'normal'
}
const barMode = ref<BarMode>(loadMode())
watch(barMode, (v) => {
  try {
    localStorage.setItem(STORAGE_KEY, v)
  } catch {
    // 保存できない環境では都度既定に戻るだけなので無視する
  }
})

const MODE_LABEL: Record<BarMode, string> = {
  normal: '通常',
  compact: 'コンパクト',
}
function cycleMode() {
  barMode.value = MODES[(MODES.indexOf(barMode.value) + 1) % MODES.length]
}

// ---- 固定バーの高さを本文側へ伝える ----
// バーは position:fixed なので、本文の下端がこの下に潜り込まないよう
// 実測した高さを CSS変数 --actionbar-h に流し込む。
// 表示モードやウィンドウ幅で高さが変わるため、ResizeObserverで追従させる。
const barEl = ref<HTMLElement | null>(null)
let ro: ResizeObserver | null = null

function syncHeight() {
  const h = barEl.value?.offsetHeight ?? 0
  document.documentElement.style.setProperty('--actionbar-h', `${h}px`)
}

onMounted(() => {
  syncHeight()
  if (barEl.value && typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(syncHeight)
    ro.observe(barEl.value)
  }
})
onBeforeUnmount(() => {
  ro?.disconnect()
  document.documentElement.style.removeProperty('--actionbar-h')
})
</script>

<template>
  <!-- 処理中のオーバーレイ。
       成功時のモーダルを廃止したため、何も出ないと「押せたのか分からず」
       二重に押される恐れがある。画面全体を覆って操作を止め、
       進行中であることを明示する。 -->
  <Teleport to="body">
    <div v-if="anyPending" class="busy-overlay">
      <div class="busy-box">
        <span class="spinner"></span>
        <span>処理中です…</span>
      </div>
    </div>
  </Teleport>

  <JudgmentQueueApplyModal
    v-if="judgmentQueueAsins !== null"
    :initial-asin="judgmentQueueAsins"
    region-code="jp"
    @applied="onJudgmentQueueApplied"
    @close="judgmentQueueAsins = null"
  />

  <div ref="barEl" class="actionbar" :class="`actionbar--${barMode}`">
    <!-- 表示モードの切り替え。バーの上端に小さく出す -->
    <button class="actionbar-mode" :title="`表示: ${MODE_LABEL[barMode]}（クリックで切替）`" @click="cycleMode">
      <svg class="icon icon-xs" viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"/></svg>
      {{ MODE_LABEL[barMode] }}
    </button>

    <div class="actionbar-inner">
      <div class="memo-block internal">
        <label>
          内部連絡事項
          <!-- 入力欄の外に出す。プレースホルダーだと文字を打った時点で見えなくなるため -->
          <span class="memo-hint"><b>//</b> 日付 / <b>@</b> 自分の名前</span>
          <select v-if="masters?.commentTemplatesInternal?.length" @change="insertInternalTemplate">
            <option value="">定型文を挿入…</option>
            <option v-for="t in masters.commentTemplatesInternal" :key="t.TemplateId" :value="t.TemplateId">
              {{ t.TemplateName }}
            </option>
          </select>
        </label>
        <div class="memo-input-wrap">
          <textarea
            ref="internalTextarea"
            v-model="memoHcube"
            placeholder="審査チーム内向けのメモを入力…"
            @input="internalAc.onInput()"
            @keydown="internalAc.onKeydown"
            @blur="internalAc.close()"
          ></textarea>
          <!-- 入力補完の候補。Enterで確定、Escapeで閉じる -->
          <div v-if="internalAc.suggestion.value" class="memo-suggest" @mousedown.prevent="internalAc.accept()">
            {{ internalAc.suggestion.value.label }}
            <span class="memo-suggest-key">Enter</span>
          </div>
        </div>
      </div>

      <div class="memo-block">
        <label>
          セラーへのお知らせ
          <span class="memo-hint"><b>//</b> 日付</span>
          <select v-if="masters?.commentTemplatesSeller?.length" @change="insertSellerTemplate">
            <option value="">定型文を挿入…</option>
            <option v-for="t in masters.commentTemplatesSeller" :key="t.TemplateId" :value="t.TemplateId">
              {{ t.TemplateName }}
            </option>
          </select>
        </label>
        <div class="memo-input-wrap">
          <textarea
            ref="sellerTextarea"
            v-model="memoSeller"
            placeholder="セラーに送付する内容を入力…"
            @input="sellerAc.onInput()"
            @keydown="sellerAc.onKeydown"
            @blur="sellerAc.close()"
          ></textarea>
          <div v-if="sellerAc.suggestion.value" class="memo-suggest" @mousedown.prevent="sellerAc.accept()">
            {{ sellerAc.suggestion.value.label }}
            <span class="memo-suggest-key">Enter</span>
          </div>
        </div>
      </div>

      <div class="judge-col">
        <div class="judge-status-row">
          <button
            class="btn btn-ghost btn-sm judge-queue-btn"
            :disabled="anyPending || !hasTarget"
            :title="isBulk ? `チェックした商品をジャッジ待ち申請します` : 'この商品をジャッジ待ち申請します'"
            @click="openJudgmentQueue"
          >
            <svg class="icon icon-xs" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
            ジャッジ待ち申請<template v-if="isBulk">（{{ selection.checkedCount }}件）</template>
          </button>
          <span
            class="status-pill"
            :class="{
              ok: detail?.offer?.JudgmentStatus === '2',
              ng: detail?.offer?.JudgmentStatus === '9',
              lng: detail?.offer?.JudgmentStatus === '8',
            }"
          >
            {{ detail?.offer ? (statusLabel[detail.offer.JudgmentStatus] ?? detail.offer.JudgmentStatus) : '--' }}
          </span>
          <!-- 販売状態の補助表示。一時停止中/削除済み/無効化中のときだけ出す(通常時は非表示) -->
          <span v-if="detail?.offer?.DeleteFlg === '2'" class="status-pill status-pill-sub">一時停止中</span>
          <span v-else-if="detail?.offer?.DeleteFlg === '1'" class="status-pill status-pill-sub">削除済み</span>
          <span v-if="detail?.offer?.EnableFlg === '0'" class="status-pill status-pill-sub">無効化中</span>
        </div>

        <!-- 補助操作: 差し戻し・更新申請・一時停止・削除・販売無効化 -->
        <div class="judge-buttons secondary">
          <button class="btn btn-ghost btn-sm" :disabled="anyPending || !hasTarget" @click="confirmAndJudge('SEND_BACK', '差し戻し')">差し戻し</button>
          <button class="btn btn-ghost btn-sm" :disabled="anyPending || !hasTarget" @click="applyRenewal">更新申請</button>
          <button
            class="btn btn-sm"
            :class="stopButtonState.variant === 'undo' ? 'btn-toggle-undo' : 'btn-ghost'"
            :disabled="anyPending || !hasTarget"
            :title="stopButtonState.variant === 'undo' ? '現在の状態を元に戻します' : '一時的に販売を停止します'"
            @click="toggleStop"
          >{{ stopButtonState.label }}</button>
          <button
            class="btn btn-sm"
            :class="enableButtonState.variant === 'undo' ? 'btn-toggle-undo' : 'btn-ghost'"
            :disabled="anyPending || !hasTarget"
            :title="enableButtonState.variant === 'undo' ? '現在の状態を元に戻します' : '販売を無効化します'"
            @click="toggleEnable"
          >{{ enableButtonState.label }}</button>
          <button
            class="btn btn-ghost btn-sm btn-danger-ghost"
            :disabled="anyPending || !hasTarget || (!isBulk && isAlreadyDeleted)"
            :title="!isBulk && isAlreadyDeleted ? '既に削除済みです' : ''"
            @click="deleteOffer"
          >{{ !isBulk && isAlreadyDeleted ? '削除済み' : '削除' }}</button>
        </div>

        <!-- 主要審査アクション -->
        <div class="judge-buttons">
          <button class="btn btn-ghost btn-lg" :disabled="anyPending || !hasTarget" @click="save">保存</button>
          <button class="btn btn-lg btn-lng" :disabled="anyPending" @click="confirmAndJudge('LISTING_NG', '出品NG')">出品NG</button>
          <button class="btn btn-lg btn-ng" :disabled="anyPending" @click="confirmAndJudge('NG', '審査NG')">審査NG</button>
          <button class="btn btn-lg btn-hold" :disabled="anyPending" @click="confirmAndJudge('HOLD', '審査保留')">審査保留</button>
          <button class="btn btn-lg btn-ok" :disabled="anyPending" @click="confirmAndJudge('OK', '審査OK')">審査OK</button>
        </div>
      </div>
    </div>

  </div>
</template>
